import { axialHexDistance } from '@/game/hexmap/coords';
import type { RuleSet } from '@/game/rules';
import {
  type EntityId,
  isCity,
  isMilitary,
  type PlayerId,
  type SimState,
  SPAWN_RADIUS,
} from '@/game/sim';
import { barracksFullText } from '@/game/view/reasons';

/**
 * Soldiers waiting outside a full barracks, by city (pure, no React): own
 * military units homed on an own completed city (where they were trained),
 * standing idle near it (no order, path, group or automation, within
 * `SPAWN_RADIUS` of its tile, where a spawn that found the stores full puts
 * them) while every store of that city that takes their type is full. The
 * sim keeps no flag for this, so a soldier the player parked there idle
 * counts too: either way it stays outside until moved onto the city or
 * barracks (the sim never moves a standing unit in); once a slot frees it
 * drops off this list. Ids in mint order.
 */
export function waitingOutside(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId
): Map<EntityId, EntityId[]> {
  const result = new Map<EntityId, EntityId[]>();
  // One check per city and type: every unit of it shares the answer.
  const full = new Map<string, boolean>();
  const units = Object.values(state.units).sort(
    (a, b) => Number(a.id.slice(1)) - Number(b.id.slice(1))
  );
  for (const u of units) {
    if (u.owner !== playerId || u.inside || u.order || u.auto || u.groupId !== undefined) continue;
    if ((u.path?.length ?? 0) > 0 || !isMilitary(rules, u.type)) continue;
    const city = u.homeCityId ? state.buildings[u.homeCityId] : undefined;
    if (!city || city.owner !== playerId || city.buildProgress < 1 || !isCity(city, rules)) {
      continue;
    }
    if (axialHexDistance(u.pos, city.pos) > SPAWN_RADIUS) continue;
    const key = `${city.id}:${u.type}`;
    if (!full.has(key)) full.set(key, barracksFullText(state, rules, city.id, u.type) !== null);
    if (!full.get(key)) continue;
    result.set(city.id, [...(result.get(city.id) ?? []), u.id]);
  }
  return result;
}
